import { GameAnalysisStatusEnum } from "libs/apiClient";
import {
	DiscoverClubTeamFormField,
	DiscoverClubTeamSide,
} from "../../discover/gamesTable/v2/constants";

/** Alias for {@link DiscoverClubTeamFormField} (grid search filter `field` values). */
export const DiscoverGamesClubTeamSearchFilterField = DiscoverClubTeamFormField;

export type DiscoverGamesClubTeamSearchFilterField = DiscoverClubTeamFormField;

export const DISCOVER_GAMES_CLUB_TEAM_SEARCH_FILTER_FIELDS: DiscoverClubTeamFormField[] = [
	DiscoverClubTeamFormField.HomeClub,
	DiscoverClubTeamFormField.HomeTeam,
	DiscoverClubTeamFormField.AwayClub,
	DiscoverClubTeamFormField.AwayTeam,
];

export enum DiscoverGameAnalysisProvider {
	Bepro = "BEPRO",
	Korastats = "KORASTATS",
	Sportlogiq = "SPORTLOGIQ",
	Ubitrack = "UBITRACK",
}

/** Provider tag filter values (aligns with `DiscoverGamesFilterAppliers::providerTag()`). */
export enum DiscoverGamesProviderTagFilterValue {
	Any = "ANY",
	Bepro = DiscoverGameAnalysisProvider.Bepro,
	Korastats = DiscoverGameAnalysisProvider.Korastats,
	Sportlogiq = DiscoverGameAnalysisProvider.Sportlogiq,
	Ubitrack = DiscoverGameAnalysisProvider.Ubitrack,
	None = "NONE",
}

/** Inline-editable discover game PATCH `requestedFields` values (aligns with `DiscoverGamePatchFieldEnum`). */
export enum DiscoverGameInlinePatchField {
	Priority = "priority",
	GameStatus = "gameStatus",
	ClubManagerId = "clubManagerId",
	ContentManagerId = "contentManagerId",
	AssigneeId = "assigneeId",
	IsVip = "isVip",
}

/** Filterable fields for the discover games grid (aligns with `DiscoverGamesFilterEnum`). */
export enum DiscoverGamesFilterField {
	Id = "id",
	Date = "date",
	CreatedAt = "createdAt",
	AgeCategory = "ageCategory",
	AnyClubId = "anyClubId",
	AnyTeamId = "anyTeamId",
	IsVip = "isVip",
	IsHidden = "isHidden",
	IsHiddenForScout = "isHiddenForScout",
	IsDiscover = "isDiscover",
	ProviderTag = "providerTag",
	HasVideo = "hasVideo",
	AnalysisRequestedAt = "analysisRequestedAt",
	GameAnalysisStatus = "gameAnalysisStatus",
	Priority = "priority",
	GameStatus = "gameStatus",
	RequestedProcessingLevel = "requestedProcessingLevel",
	Owner = "owner",
	Country = "country",
	ClubManager = "clubManager",
	ContentManager = "contentManager",
	Assignee = "assignee",
}

/** API column / sort / `requiredFields` values (aligns with `DiscoverGamesColumnEnum`). */
export enum DiscoverGamesColumnField {
	Id = "id",
	Date = "date",
	CreatedAt = "createdAt",
	AgeCategory = "ageCategory",
	HomeClubCountry = "homeClubCountry",
	AwayClubCountry = "awayClubCountry",
	HomeTeamCountry = "homeTeamCountry",
	AwayTeamCountry = "awayTeamCountry",
	HomeClubId = "homeClubId",
	HomeClubName = "homeClubName",
	HomeGameTeamClubName = "homeGameTeamClubName",
	HomeTeamId = "homeTeamId",
	HomeTeamName = "homeTeamName",
	HomeGameTeamTeamName = "homeGameTeamTeamName",
	HomeScore = "homeScore",
	HomeIsTeamsheetFilled = "homeIsTeamsheetFilled",
	HomePlayersCount = "homePlayersCount",
	AwayClubId = "awayClubId",
	AwayClubName = "awayClubName",
	AwayGameTeamClubName = "awayGameTeamClubName",
	AwayTeamId = "awayTeamId",
	AwayTeamName = "awayTeamName",
	AwayGameTeamTeamName = "awayGameTeamTeamName",
	AwayScore = "awayScore",
	AwayIsTeamsheetFilled = "awayIsTeamsheetFilled",
	AwayPlayersCount = "awayPlayersCount",
	ClubManagerId = "clubManagerId",
	ClubManagerName = "clubManagerName",
	ContentManagerId = "contentManagerId",
	ContentManagerName = "contentManagerName",
	AssigneeId = "assigneeId",
	AssigneeName = "assigneeName",
	IsVip = "isVip",
	IsHidden = "isHidden",
	IsHiddenForScout = "isHiddenForScout",
	IsDiscover = "isDiscover",
	ProviderTag = "providerTag",
	HasVideo = "hasVideo",
	AnalysisRequestedAt = "analysisRequestedAt",
	RequestedProcessingLevel = "requestedProcessingLevel",
	GameAnalysisStatus = "gameAnalysisStatus",
	Priority = "priority",
	AnalysisTurnaround = "analysisTurnaround",
	AnalysisSlaHours = "analysisSlaHours",
	AnalysisSla = "analysisSla",
	GameStatus = "gameStatus",
	GameHistoriesCount = "gameHistoriesCount",
	PhysicalMetricsRequested = "physicalMetricsRequested",
	Owners = "owners",
	SlaContent = "slaContent",
	SlaExpansion = "slaExpansion",
	NumberOfContentErrors = "numberOfContentErrors",
	NumberOfTaggingErrors = "numberOfTaggingErrors",
}

/** Composite column `key` values (UI-only; not API column ids). */
export enum DiscoverGamesUiColumnKey {
	Score = "score",
	Country = "country",
	TeamSheet = "teamSheet",
	Home = "home",
	Away = "away",
	ClubManager = "clubManager",
	ContentManager = "contentManager",
	Assignee = "assignee",
}

/** Per-side `GameRow` fields used by home/away team columns (`teamColumns.tsx`). */
export const DISCOVER_GAMES_TEAM_SIDE_ROW_FIELDS = {
	[DiscoverClubTeamSide.Home]: {
		clubId: DiscoverGamesColumnField.HomeClubId,
		clubName: DiscoverGamesColumnField.HomeClubName,
		fallbackClubName: DiscoverGamesColumnField.HomeGameTeamClubName,
		teamId: DiscoverGamesColumnField.HomeTeamId,
		teamName: DiscoverGamesColumnField.HomeTeamName,
		fallbackTeamName: DiscoverGamesColumnField.HomeGameTeamTeamName,
	},
	[DiscoverClubTeamSide.Away]: {
		clubId: DiscoverGamesColumnField.AwayClubId,
		clubName: DiscoverGamesColumnField.AwayClubName,
		fallbackClubName: DiscoverGamesColumnField.AwayGameTeamClubName,
		teamId: DiscoverGamesColumnField.AwayTeamId,
		teamName: DiscoverGamesColumnField.AwayTeamName,
		fallbackTeamName: DiscoverGamesColumnField.AwayGameTeamTeamName,
	},
} as const;

/** Column id for the bulk-selection checkbox column (not a backend field). */
export const DISCOVER_GAMES_BULK_SELECT_KEY = "bulkSelect";

/** Fallback column group when `ColumnConfig.group` is unset (visibility modal + group color grading). */
export const DISCOVER_GAMES_DEFAULT_COLUMN_GROUP = "Default";

/** Id column (fixed min and max). */
export const DISCOVER_GAMES_COL_WIDTH_ID = { min: 90, max: 120 } as const;

/** Game date/time column (formatted datetime string). */
export const DISCOVER_GAMES_COL_WIDTH_DATE = { min: 160, max: 200 } as const;

/** Score column ("home : away", centered). */
export const DISCOVER_GAMES_COL_WIDTH_SCORE = { min: 72, max: 100 } as const;

/** Home side column (club + team links, multi-line). */
export const DISCOVER_GAMES_COL_WIDTH_HOME = { min: 240, max: 320 } as const;

/** Away side column (club + team links, multi-line). */
export const DISCOVER_GAMES_COL_WIDTH_AWAY = { min: 240, max: 320 } as const;

/** Bulk checkbox column. */
export const DISCOVER_GAMES_COL_WIDTH_BULK_SELECT = { min: 60, max: 80 } as const;

/** Fallback widths (px) for data columns when no stored/config width exists (SimpleColumn builder). */
export const DISCOVER_GAMES_DATA_COLUMN_FALLBACK = { min: 50, max: 150 } as const;

/** Manager dropdown columns (club / content / assignee). */
export const DISCOVER_GAMES_COL_WIDTH_MANAGER = { min: 300, max: 620 } as const;

export const DISCOVER_GAMES_COL_WIDTH_PRIORITY = { min: 160, max: 180 } as const;
export const DISCOVER_GAMES_COL_WIDTH_GAME_STATUS = { min: 320, max: 440 } as const;
export const DISCOVER_GAMES_COL_WIDTH_HISTORIES = { min: 120, max: 140 } as const;
export const DISCOVER_GAMES_COL_WIDTH_TURNAROUND = { min: 120, max: 160 } as const;
export const DISCOVER_GAMES_COL_WIDTH_SLA = { min: 140, max: 160 } as const;
/** Error-count cells hold a single number but carry long headers ("Number of content errors"). */
export const DISCOVER_GAMES_COL_WIDTH_ERRORS = { min: 120, max: 140 } as const;

/** Boolean cell columns (yes/no icon). Header text varies in length. */
export const DISCOVER_GAMES_COL_WIDTH_BOOLEAN = { min: 90, max: 140 } as const;

/** VIP column: editable Yes/No dropdown needs room for the react-select control (matches priority). */
export const DISCOVER_GAMES_COL_WIDTH_VIP = { min: 160, max: 180 } as const;

/** Physical metrics requested (boolean cell, long header label). */
export const DISCOVER_GAMES_COL_WIDTH_PHYSICAL_METRICS_REQUESTED = { min: 140, max: 220 } as const;

/** Short single-token label columns (country code(s), age category, discover/elite). */
export const DISCOVER_GAMES_COL_WIDTH_LABEL_SHORT = { min: 160, max: 200 } as const;

/** Processing level label (single word, mid-length headers). */
export const DISCOVER_GAMES_COL_WIDTH_PROCESSING_LEVEL = { min: 120, max: 180 } as const;

/** Team sheet summary cell: "✓ (11) | ✓ (11)" or similar. */
export const DISCOVER_GAMES_COL_WIDTH_TEAM_SHEET = { min: 140, max: 200 } as const;

/** Analysis provider tag (provider name string). */
export const DISCOVER_GAMES_COL_WIDTH_PROVIDER_TAG = { min: 140, max: 200 } as const;

/** Game analysis status label (e.g. Requested / In Progress / Done). */
export const DISCOVER_GAMES_COL_WIDTH_GAME_ANALYSIS_STATUS = { min: 160, max: 220 } as const;

/** Owners list (comma-separated names; can get long). */
export const DISCOVER_GAMES_COL_WIDTH_OWNERS = { min: 200, max: 360 } as const;

/** Date-only columns (no time component), e.g. analysis requested at. */
export const DISCOVER_GAMES_COL_WIDTH_DATE_SHORT = { min: 110, max: 160 } as const;

/** Select options for per-game priority filter (0–10 on `GameMetadata.priority`). */
export const DISCOVER_GAMES_PRIORITY_FILTER_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
	value: i,
	label: String(i),
}));

/** Select options for per-game priority inline-edit (0–10). */
export const PRIORITY_SELECT_ITEMS = Array.from({ length: 11 }, (_, i) => ({
	value: String(i),
	label: String(i),
}));

/**
 * Last `GameAnalysis.status` values where priority inline-edit must be disabled.
 * Mirrors `Eyeball\Enum\GameAnalysisStatus` (`Done`, `Rejected`, `Requested`).
 */
export const GAME_ANALYSIS_STATUS_BLOCKING_PRIORITY_EDIT = new Set<string>([
	GameAnalysisStatusEnum.Values.Done,
	GameAnalysisStatusEnum.Values.Rejected,
	GameAnalysisStatusEnum.Values.Requested,
]);

export const ANALYSIS_TURNAROUND_CAP_LABEL = "99:59+";

/** Passed to `dateTimeFormatUtils` for Discover Games SLA / turnaround display. */
export const DISCOVER_GAMES_DURATION_CAP_OPTIONS = {
	maxDisplayHours: 99,
	capLabel: ANALYSIS_TURNAROUND_CAP_LABEL,
};
